/**
 * Account-first signup (MyGate model). Creates a society-agnostic Bero account —
 * name, email, phone — then sends the person to add their first flat. Society
 * membership is a separate, admin-approved step (/account/add-flat), not part of
 * creating the account. Passwordless: we email a 6-digit code and finish on
 * /verify-code (which creates the account with this name + phone). This is the
 * primary "Create account" entry from the login screen; the older society-first
 * onboarding wizard still exists for the flows that pass a society through.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { useSession } from '@/lib/session';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, Field, Input, Screen, TopBar } from '@/ui';

type Errors = { fullName?: string; email?: string; phone?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CreateAccountScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { requestCode } = useSession();

  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [errors, setErrors] = React.useState<Errors>({});
  const [notice, setNotice] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    const e: Errors = {};
    if (!fullName.trim()) e.fullName = t('onb_signup_err_name');
    if (!EMAIL_RE.test(email.trim())) e.email = t('onb_signup_err_email');
    if (phone.trim() && phone.trim().replace(/\D/g, '').length < 7) e.phone = t('onb_signup_err_phone');
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    setNotice(null);
    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = phone.trim() ? `+91${phone.trim().replace(/\D/g, '')}` : undefined;
    try {
      // Passwordless: request a code, then finish on /verify-code, which creates
      // the account with this name + phone. An existing email just signs in.
      await requestCode(cleanEmail);
      router.push({
        pathname: '/verify-code',
        params: { email: cleanEmail, name: fullName.trim(), ...(cleanPhone ? { phone: cleanPhone } : {}) },
      });
    } catch (err) {
      setNotice((err as { error?: string }).error ?? t('onb_signup_err_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('ca_title')} onBack={() => router.replace('/login')} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <AppText variant="bodyMd" tone="secondary">{t('ca_sub')}</AppText>

        <Field label={t('onb_signup_name_label')} error={errors.fullName}>
          <Input value={fullName} onChangeText={setFullName} placeholder={t('onb_signup_name_ph')} hasError={!!errors.fullName} />
        </Field>
        <Field label={t('onb_signup_email_label')} error={errors.email}>
          <Input
            value={email}
            onChangeText={setEmail}
            placeholder="you@example.com"
            autoCapitalize="none"
            keyboardType="email-address"
            autoComplete="email"
            hasError={!!errors.email}
          />
        </Field>
        <Field label={t('onb_signup_phone_label')} error={errors.phone} help={t('onb_signup_phone_help')}>
          <View style={{ flexDirection: 'row', gap: space.s2 }}>
            <View style={{
              width: 56, minHeight: 44, borderRadius: 8, borderWidth: 1, borderColor: colors.border,
              alignItems: 'center', justifyContent: 'center', backgroundColor: colors.canvas0,
            }}>
              <AppText variant="bodyMd" tone="secondary">+91</AppText>
            </View>
            <Input value={phone} onChangeText={setPhone} placeholder="98XXXXXX21" keyboardType="phone-pad" style={{ flex: 1 }} hasError={!!errors.phone}
              returnKeyType="go" onSubmitEditing={() => void submit()} />
          </View>
        </Field>
        {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}

        <Button title={t('ca_submit')} size="lg" loading={submitting} onPress={() => void submit()} style={{ marginTop: space.s2 }} />
        <AppText variant="bodySm" tone="tertiary" style={{ textAlign: 'center' }}>{t('ca_next_note')}</AppText>
      </Screen>
    </Screen>
  );
}
